import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import foodImage from "@/assets/bar-food.jpg";

interface FoodItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
}

export function FoodMenu() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [foodItems, setFoodItems] = useState<FoodItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFood();
  }, []);

  const fetchFood = async () => {
    const { data, error } = await supabase
      .from('food_items')
      .select('*')
      .eq('is_available', true)
      .order('sort_order');

    if (!error && data) {
      setFoodItems(data);
    }
    setLoading(false);
  };

  return (
    <section id="food" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />

      <div className="container mx-auto px-4 relative" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="text-primary font-medium tracking-[0.2em] text-sm mb-4">BAR BITES</p>
            <h2 className="section-heading text-foreground mb-6">
              FOOD<br /><span className="text-gradient-gold">MENU</span>
            </h2>
            <p className="text-foreground/70 text-lg leading-relaxed mb-10">
              Perfect companions for your drinks. Our bar snacks are crafted to complement your cocktails.
            </p>

            <div className="space-y-4">
              {loading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-card/50 animate-pulse rounded" />
                ))
              ) : (
                foodItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                    className="flex gap-4 pb-4 border-b border-border/30 group hover:border-primary/30 transition-colors"
                  >
                    {item.image_url && (
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    )}
                    <div className="flex-1 flex justify-between items-start">
                      <div className="flex-1 pr-4">
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                          {item.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                      </div>
                      <span className="text-primary font-display text-xl whitespace-nowrap">฿{item.price}</span>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative lg:order-first xl:order-last"
          >
            <div className="relative aspect-square rounded-lg overflow-hidden">
              <img src={foodImage} alt="Bar Food" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
            </div>
            <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-primary/30 rounded-lg" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-primary/30 rounded-lg" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}